import {Box, useDisclosure} from '@chakra-ui/react';
import {useProductByName, useRestaurantBySlug, useRestaurantDistancePrice} from 'api/queries';
import {useProduct} from 'api/queries/product/useProduct';
import {getSeoBySlug} from 'api/requests';
import {DefaultLayout} from 'components/layouts';
import {
  AlertAdultProducts,
  AlertClearCart,
  CategorySubheader,
  ClosedProviderBanner,
  Products,
  Promotions,
  ProviderInfo,
  ProviderPreview,
} from 'components/pages/restaurant';
import {useURLSpecificParams} from 'components/pages/restaurant/hooks';
import {NotFoundBanner} from 'components/pages/search/not-found-banner';
import {useCartContext} from 'components/providers';
import {CategorySubheaderSkeleton, Head, ProviderHeadSkeleton, SeoMetadata} from 'components/shared';
import {DEFAULT_COORDINATES} from 'config';
import {useAuth, useDesktop, useEditFavorites, useLocalStorageState, useTranslation} from 'hooks';
import {GetServerSideProps, type NextPage} from 'next';
import {useRouter} from 'next/router';
import {useEffect, useMemo, useState} from 'react';
import GTMService from 'services/ga4';
import {
  ICartItem,
  IProduct,
  IProductsResponse,
  IRestaurantDistancePriceResponse,
  ISeoMetadata,
  ProviderTypeEnum,
  QueryKeyEnum,
  StorageKeyEnum,
} from 'types';
import logger from 'utils/logger';
import {
  filterSearch,
  getCorrectProviderSlug,
  getNormalizedCrossSelling,
  prepareCategoriesWithProducts,
} from 'utils/provider';
import {replaceProviderSeoWith} from 'utils/seo';

interface Props {
  seoMetadata?: ISeoMetadata;
}
const RestaurantPage: NextPage<Props> = ({seoMetadata}) => {
  const {t} = useTranslation();
  const {isOpen: isOpenProductModal, onOpen: onOpenProductModal, onClose: onCloseProductModal} = useDisclosure();
  const {isOpen: isOpenAlertClearCart, onOpen: onOpenAlertClearCart, onClose: onCloseAlertClearCart} = useDisclosure();
  const {isOpen: isOpenClosedBanner, onOpen: onOpenClosedBanner, onClose: onCloseBanner} = useDisclosure();
  const {
    isOpen: isOpedAdultProductsBanner,
    onOpen: onOpedAdultProductsBanner,
    onClose: onCloseAdultProductsBanner,
  } = useDisclosure();
  const {push, asPath: path} = useRouter();

  const {cartProvider, addProvider, addProductToCart, cartItems} = useCartContext();

  const [selectedProduct, setSelectedProduct] = useState<IProduct>();
  const [tempProduct, setTempProduct] = useState<IProduct>();
  const {isAuthenticated} = useAuth();
  const {myFavorite, onEditFavorites} = useEditFavorites();
  const isDesktop = useDesktop();
  const {query, locale} = useRouter();
  const isMarket = query.providerType === ProviderTypeEnum.MARKET;
  const slugArr = `${query?.slug}`.split('=');

  const productSlug = !!slugArr?.length ? slugArr[slugArr.length] : undefined;
  const fakeId = query?.fake_id;

  const {data: providerSlugResponse} = useProductByName(
    [QueryKeyEnum.PRODUCT_BY_NAME, productSlug, locale],
    {slug: `${productSlug}`},
    {
      enabled: !!productSlug,
      onError: () => {
        // push('/not-found');
      },
    },
  );

  const providerSlug = getCorrectProviderSlug(query, slugArr, providerSlugResponse);
  const [coordinatesAddress] = useLocalStorageState(StorageKeyEnum.COORDINATES);
  const coordinates = `${coordinatesAddress ?? DEFAULT_COORDINATES}`;

  const cleanedPath = path.replace(/^\/?restaurant\//, '');

  const {data: providerData, isLoading: isLoadingProviderData} = useRestaurantBySlug(
    [QueryKeyEnum.RESTAURANT_BY_SLUG, providerSlug, locale, coordinates],
    {slug: fakeId ? providerSlug + `?fake_id=${fakeId}` : providerSlug},
    {
      onError: () => {
        push(`/not-found-provider/${cleanedPath}`);
      },
    },
    locale ?? '',
    `${coordinates}`,
    !!query.previewId,
  );

  const {data: productsData, isLoading: isLoadingProducts} = useProduct(
    [QueryKeyEnum.PRODUCTS, providerData?.slug, locale],
    {provider__slug: `${providerData?.slug}`, fake_id: fakeId},
    {
      enabled: !!providerData?.slug,
    },
  );

  const {
    data: dynamicDeliveryPrice = {
      surcharge: 0,
      surcharge_for_out_area: 0,
    } as IRestaurantDistancePriceResponse,
  } = useRestaurantDistancePrice({
    providerSlug: providerSlug,
  });

  const {surcharge, surcharge_for_out_area} = dynamicDeliveryPrice;
  const hasAdultProducts = providerData?.has_adult_products;

  const acceptsPreorder = !!providerData?.accepts_preorders;
  const isProviderClosed = !providerData?.is_open && !acceptsPreorder;
  const isProviderOnPause = !!providerData?.is_on_pause;
  const isDisabledProvider = isProviderClosed || isProviderOnPause;

  const {products} = productsData ?? ({} as IProductsResponse);

  const crossSelling = useMemo(
    () => getNormalizedCrossSelling(productsData, selectedProduct?.menu_category?.id),
    [selectedProduct, productsData],
  );

  useEffect(() => {
    //OPEN ProductDetailsModal BY PRODUCT SLUG
    logger.logDebug(`${productSlug}`, 'SLUG');
    if (productSlug) openProductDetailsModalHandler(productSlug);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, productSlug]);

  useEffect(() => {
    if (!productSlug && !providerSlug) push(`/not-found-provider/${cleanedPath}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openProductDetailsModalHandler = (productSlug: string) => {
    if (productSlug && products) {
      for (let i = 0; products.length > i; i++) {
        if (products[i].slug === productSlug) {
          setSelectedProduct(products[i]);
          onOpenProductModal();
          changeURLRelativePath({productId: products[i].id});
        }
      }
    }
  };

  const [onlyOnce, setonlyOnce] = useState(false);
  useEffect(() => {
    const finalPath = ['restaurant'];
    if (onlyOnce) return;
    if (!finalPath || !providerData) return;
    GTMService.viewItemList(
      {
        category: 'restaurants',
        path: finalPath,
      },
      {id: providerData.id, name: providerData.name, slug: providerData.slug},
    );

    setonlyOnce(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerData, onlyOnce]);

  const categoriesWithProducts = prepareCategoriesWithProducts(productsData, providerData, t);
  const sortedCategoriesWithProducts = query.query
    ? filterSearch(categoriesWithProducts, query.query)
    : categoriesWithProducts;
  const categoryList = query.query ? sortedCategoriesWithProducts : categoriesWithProducts;

  useEffect(() => {
    if (!providerData) return;
    if (!providerData.is_open || providerData.is_on_pause) return onOpenClosedBanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerData]);

  useEffect(() => {
    if (hasAdultProducts) onOpedAdultProductsBanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAdultProducts]);

  const handleProductClick = (product: IProduct) => {
    if (providerData?.id !== cartProvider?.id && cartProvider) {
      setTempProduct(product);
      return onOpenAlertClearCart();
    }
    onOpenProductModal();
    if (product) changeURLRelativePath({productId: product.id});

    setSelectedProduct(product);
  };

  const {changeURLRelativePath} = useURLSpecificParams({
    products: products,
    onProductSelect: handleProductClick,
  });

  const handleCloseModal = () => {
    if (!isOpenAlertClearCart) {
      onCloseProductModal();
      setSelectedProduct(undefined);
      changeURLRelativePath({});
    }
  };

  const handleAddProduct = (cartItem: ICartItem, crosss?: ICartItem[]) => {
    if (providerData?.is_on_pause || (!providerData?.is_open && !providerData?.accepts_preorders))
      return onOpenClosedBanner();
    if (!providerData) return;
    if (!cartProvider) addProvider(providerData);

    let productTracking = 'restaurant/';
    // Tracking product origin - added from carousel, crosseling, recommended
    const isFromReccomended = providerData?.recommended_products.find((item) => item.id === cartItem.product.id);
    productTracking += isFromReccomended ? 'restaurant_recommended' : 'restaurant';

    cartItem.added_from = productTracking;
    addProductToCart(cartItem, crosss);
  };

  const onOpenProductDetailsModalForPromo = (productId: number) => {
    for (let i = 0; products.length > i; i++) {
      if (products[i].id === productId) {
        handleProductClick(products[i]);
        break;
      }
    }
  };

  const titlePage = providerData?.name ? providerData.name : `${t('SEARCH')}...`;

  const providerMenu =
    categoryList.length === 1
      ? categoryList[0].name
      : categoryList
          .slice(1, 5)
          .map((category) => category.name)
          .join(', ');

  return (
    <DefaultLayout
      isDesktop={isDesktop}
      providerName={providerData?.name ?? ''}
      subheader={
        !!productsData ? (
          <CategorySubheader categoryList={categoryList} isSearchable={!!query.query} />
        ) : (
          <CategorySubheaderSkeleton />
        )
      }
    >
      <Head title={titlePage} />
      <SeoMetadata data={replaceProviderSeoWith(seoMetadata, providerData?.name, providerMenu, providerSlug)} />
      <Box px={['15px', '30px', '60px']} py="30px" pt={['0px', 'auto']} id="head">
        {providerData ? (
          <ProviderPreview
            myFavorite={myFavorite.find((items) => items.slug === providerData.slug)}
            onEditFavorite={onEditFavorites}
            isLoggedIn={isAuthenticated}
            provider={providerData}
            dynamicDeliveryPrice={surcharge_for_out_area + surcharge}
          />
        ) : (
          <ProviderHeadSkeleton />
        )}

        {providerData?.info && <ProviderInfo info={providerData.info} />}

        {!!providerData?.promotions?.length && (
          <Promotions
            promotions={providerData?.promotions}
            onOpenProductDetailsModal={onOpenProductDetailsModalForPromo}
          />
        )}
        {providerData && sortedCategoriesWithProducts?.length === 0 && !isLoadingProducts && (
          <NotFoundBanner icon="/icons/search-not-found.svg" title={t('SEARCH_NOT_FOUND')} />
        )}
        <Products
          sortedCategoriesWithProducts={sortedCategoriesWithProducts}
          cartItems={cartItems}
          locale={locale}
          isLoadingProducts={isLoadingProducts || isLoadingProviderData}
          isMarket={isMarket}
          isOpenProductModal={isOpenProductModal}
          isDisabledProvider={isDisabledProvider}
          isDisabledAddProduct={!providerData?.is_open && !providerData?.accepts_preorders}
          t={t}
          providerSlug={providerData?.slug}
          crossSelling={crossSelling}
          selectedProduct={selectedProduct}
          handleCloseModal={handleCloseModal}
          handleProductClick={handleProductClick}
          handleAddProduct={handleAddProduct}
          provider={providerData}
        />
      </Box>

      {/* MODALS */}
      {providerData && (
        <ClosedProviderBanner
          t={t}
          isOpen={isOpenClosedBanner}
          onClose={onCloseBanner}
          name={providerData.name}
          timetable={providerData.timetable}
          providerType={providerData?.provider_type}
          acceptsPreorder={acceptsPreorder}
          isOnPause={isProviderOnPause}
          message={providerData.message}
        />
      )}

      {isOpenAlertClearCart && (
        <AlertClearCart
          isOpen={isOpenAlertClearCart}
          onClose={onCloseAlertClearCart}
          onConfirm={() => {
            if (!tempProduct) return;
            setSelectedProduct(tempProduct);
            onOpenProductModal();
          }}
          providerName={cartProvider?.name}
        />
      )}
      {isOpedAdultProductsBanner && <AlertAdultProducts onConfirm={onCloseAdultProductsBanner} />}
    </DefaultLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({locale}) => {
  const seoMetadata = await getSeoBySlug('providers', locale);

  return {
    props: {
      seoMetadata,
    },
  };
};

export default RestaurantPage;
